import { Image } from 'react-native';

import type { OralExamAggregateTimeSlotStateResponse } from '@/common/api/models/oralExamAggregateTimeSlotStateResponse';
import type { OralExamBookingRunSummary } from '@/common/api/models/oralExamBookingRunSummary';
import { OralExamMode } from '@/common/api/models/oralExamMode';
import { OralExamReadiness } from '@/common/api/models/oralExamReadiness';
import type { OralExamRunStateResponse } from '@/common/api/models/oralExamRunStateResponse';
import { OralExamSessionPhase } from '@/common/api/models/oralExamSessionPhase';
import type { MeetingJoinSettings } from '@/common/components/meeting/types';
import type {
  OralExamLabCohortState,
  OralExamLabImageSource,
  OralExamLabOverridesValue,
} from '@/common/oralExam/oralExamLabOverrides';
import type { GetOralExamIdPhotoUrlKind } from '@/common/api/models/getOralExamIdPhotoUrlKind';

import {
  type PatrolLabPreflightState,
  type PatrolLabQuery,
} from '@/visual-lab/chapters/patrol/parsePatrolLabQuery';
import benIdCard from '@/visual-lab/chapters/patrol/assets/ben-kowalski-id-card.png';
import thomasIdCard from '@/visual-lab/chapters/patrol/assets/thomas-wilde-id-card.png';
import { PATROL_LAB_STANDINS } from '@/visual-lab/chapters/patrol/patrolLabStandIns';

export const PREFLIGHT_LAB_SLOT_ID = 'preflight-lab-slot';
export const PREFLIGHT_LAB_THOMAS_BOOKING_ID = 'booking-thomas';
export const PREFLIGHT_LAB_BEN_BOOKING_ID = 'booking-ben';
export const PREFLIGHT_LAB_CARLA_BOOKING_ID = 'booking-carla';
export const PREFLIGHT_LAB_CAPACITY = 3;
export const PREFLIGHT_LAB_SLOT_START_OFFSET_MS = 10 * 60 * 1000;
/** `late`: the scheduled start passed a minute ago and the join grace is running. */
export const PREFLIGHT_LAB_LATE_START_OFFSET_MS = -1 * 60 * 1000;
export const PREFLIGHT_LAB_SLOT_DURATION_MS = 60 * 60 * 1000;

/** Slot window anchored to now so join grace and scheduled start read like a live sitting. */
export function buildPreflightLabSlotWindow(
  now = Date.now(),
  startOffsetMs = PREFLIGHT_LAB_SLOT_START_OFFSET_MS,
) {
  const slotStartMs = now + startOffsetMs;
  const slotEndMs = slotStartMs + PREFLIGHT_LAB_SLOT_DURATION_MS;
  return {
    slotStartIso: new Date(slotStartMs).toISOString(),
    slotEndIso: new Date(slotEndMs).toISOString(),
    consentAt: new Date(now - 3 * 60 * 1000).toISOString(),
    reviewedAt: new Date(now - 1 * 60 * 1000).toISOString(),
  };
}

const PREFLIGHT_STUDENTS = {
  thomas: {
    bookingId: PREFLIGHT_LAB_THOMAS_BOOKING_ID,
    studentId: 'student-thomas',
    studentName: 'Thomas Wilde',
    selfie: PATROL_LAB_STANDINS.harry,
  },
  ben: {
    bookingId: PREFLIGHT_LAB_BEN_BOOKING_ID,
    studentId: 'student-ben',
    studentName: 'Ben Kowalski',
    selfie: PATROL_LAB_STANDINS.ron,
  },
  carla: {
    bookingId: PREFLIGHT_LAB_CARLA_BOOKING_ID,
    studentId: 'student-carla',
    studentName: 'Carla Santos',
    selfie: PATROL_LAB_STANDINS.hermione,
  },
} as const;

const PREFLIGHT_ID_DOCUMENTS: Record<string, OralExamLabImageSource> = {
  [PREFLIGHT_LAB_THOMAS_BOOKING_ID]: thomasIdCard,
  [PREFLIGHT_LAB_BEN_BOOKING_ID]: benIdCard,
};

function assetUri(source: OralExamLabImageSource): string {
  if (typeof source === 'string') return source;
  if (typeof source === 'number') {
    return Image.resolveAssetSource(source).uri;
  }
  return source.uri ?? '';
}

function baseRunState(bookingId: string): OralExamRunStateResponse {
  return {
    state: 'pre_start',
    sessionPhase: OralExamSessionPhase.pre_flight,
    selectedQuestionIds: [],
    draftSelectedQuestionIds: ['q1', 'q2'],
    secondsPerQuestion: 120,
    questionCount: 2,
    etag: `preflight-lab-run-${bookingId}`,
    capacity: PREFLIGHT_LAB_CAPACITY,
    mode: OralExamMode.one_to_n,
    startAuthority: 'lecturer',
    requiresEvaluation: true,
  };
}

function buildBooking(
  profile: typeof PREFLIGHT_STUDENTS[keyof typeof PREFLIGHT_STUDENTS],
  overrides: Partial<OralExamBookingRunSummary> = {},
): OralExamBookingRunSummary {
  return {
    bookingId: profile.bookingId,
    studentId: profile.studentId,
    studentName: profile.studentName,
    courseId: 'course-preflight-lab',
    moduleId: 'module-preflight-lab',
    taskId: 'task-preflight-lab',
    present: overrides.present ?? true,
    isAdmitted: false,
    hasRecording: false,
    idVerification: overrides.idVerification ?? { status: 'not_submitted' },
    idPhotoBlobPath: overrides.idPhotoBlobPath,
    idSelfiePhotoBlobPath: overrides.idSelfiePhotoBlobPath,
    readiness: overrides.readiness,
    supervisorStatus: 'watching',
    runState: {
      ...baseRunState(profile.bookingId),
      ...(overrides.runState ?? {}),
      consentRecord: overrides.runState?.consentRecord,
    },
    ...overrides,
  };
}

const PHOTO_BLOB = 'preflight-lab/id-document.jpg';
const SELFIE_BLOB = 'preflight-lab/id-selfie.jpg';

function consentRecord(grantedAt: string) {
  return {
    subject: 'student',
    grantedAt,
    policyVersion: 'v1',
    scope: 'exam',
  } as const;
}

function bookingsForState(
  state: PatrolLabPreflightState,
  slotWindow: ReturnType<typeof buildPreflightLabSlotWindow>,
): OralExamBookingRunSummary[] {
  const { thomas, ben, carla } = PREFLIGHT_STUDENTS;
  const { consentAt, reviewedAt } = slotWindow;

  switch (state) {
    case 'waiting':
      return [
        buildBooking(thomas, {
          readiness: OralExamReadiness.in_lobby,
          idVerification: { status: 'not_submitted' },
          runState: {
            ...baseRunState(thomas.bookingId),
            consentRecord: consentRecord(consentAt),
          },
        }),
      ];
    case 'ready':
      return [
        buildBooking(thomas, {
          readiness: OralExamReadiness.awaiting_id_review,
          idVerification: { status: 'submitted' },
          idPhotoBlobPath: PHOTO_BLOB,
          idSelfiePhotoBlobPath: SELFIE_BLOB,
          runState: {
            ...baseRunState(thomas.bookingId),
            consentRecord: consentRecord(consentAt),
          },
        }),
        buildBooking(ben, {
          readiness: OralExamReadiness.awaiting_id_review,
          idVerification: { status: 'submitted' },
          idPhotoBlobPath: PHOTO_BLOB,
          idSelfiePhotoBlobPath: SELFIE_BLOB,
          runState: {
            ...baseRunState(ben.bookingId),
            consentRecord: consentRecord(consentAt),
          },
        }),
      ];
    case 'mixed':
      return [
        buildBooking(ben, {
          readiness: OralExamReadiness.awaiting_id_review,
          idVerification: { status: 'submitted' },
          idPhotoBlobPath: PHOTO_BLOB,
          idSelfiePhotoBlobPath: SELFIE_BLOB,
          runState: {
            ...baseRunState(ben.bookingId),
            consentRecord: consentRecord(consentAt),
          },
        }),
        buildBooking(thomas, {
          readiness: OralExamReadiness.ready_to_start,
          idVerification: {
            status: 'approved',
            reviewedAt,
          },
          idPhotoBlobPath: PHOTO_BLOB,
          idSelfiePhotoBlobPath: SELFIE_BLOB,
          runState: {
            ...baseRunState(thomas.bookingId),
            consentRecord: consentRecord(consentAt),
          },
        }),
        buildBooking(carla, {
          readiness: OralExamReadiness.not_joined,
          present: false,
          idVerification: { status: 'not_submitted' },
        }),
      ];
    case 'retakeRequested':
      return [
        buildBooking(ben, {
          readiness: OralExamReadiness.id_rejected,
          idVerification: {
            status: 'rejected',
            rejectionReasonCode: 'blurry',
            reviewedAt,
          },
          runState: {
            ...baseRunState(ben.bookingId),
            consentRecord: consentRecord(consentAt),
          },
        }),
        buildBooking(thomas, {
          readiness: OralExamReadiness.ready_to_start,
          idVerification: {
            status: 'approved',
            reviewedAt,
          },
          idPhotoBlobPath: PHOTO_BLOB,
          idSelfiePhotoBlobPath: SELFIE_BLOB,
          runState: {
            ...baseRunState(thomas.bookingId),
            consentRecord: consentRecord(consentAt),
          },
        }),
      ];
    case 'retakeDialog':
      return bookingsForState('ready', slotWindow);
    case 'late':
      return [
        ...bookingsForState('cleared', slotWindow),
        buildBooking(carla, {
          readiness: OralExamReadiness.not_joined,
          present: false,
          idVerification: { status: 'not_submitted' },
        }),
      ];
    case 'cleared':
      return [
        buildBooking(thomas, {
          readiness: OralExamReadiness.ready_to_start,
          idVerification: {
            status: 'approved',
            reviewedAt,
          },
          idPhotoBlobPath: PHOTO_BLOB,
          idSelfiePhotoBlobPath: SELFIE_BLOB,
          runState: {
            ...baseRunState(thomas.bookingId),
            consentRecord: consentRecord(consentAt),
          },
        }),
        buildBooking(ben, {
          readiness: OralExamReadiness.ready_to_start,
          idVerification: {
            status: 'approved',
            reviewedAt,
          },
          idPhotoBlobPath: PHOTO_BLOB,
          idSelfiePhotoBlobPath: SELFIE_BLOB,
          runState: {
            ...baseRunState(ben.bookingId),
            consentRecord: consentRecord(consentAt),
          },
        }),
      ];
    default:
      return bookingsForState('ready', slotWindow);
  }
}

function resolveIdPhotoUrl(bookingId: string, kind: GetOralExamIdPhotoUrlKind): string | null {
  const profile = Object.values(PREFLIGHT_STUDENTS).find((student) => student.bookingId === bookingId);
  if (!profile) return null;
  if (kind === 'document') {
    const document = PREFLIGHT_ID_DOCUMENTS[bookingId];
    return document ? assetUri(document) : null;
  }
  return assetUri(profile.selfie);
}

export type PreflightLabFixture = {
  slotId: string;
  slotStartIso: string;
  slotEndIso: string;
  joinSettings: MeetingJoinSettings;
  joinCreds: null;
  overrides: OralExamLabOverridesValue;
};

/** Visual Lab fixture for production LecturerPreflightStage commissioning. */
export function buildPreflightLabFixture(query: PatrolLabQuery): PreflightLabFixture {
  const state = query.state ?? 'ready';
  const slotWindow = buildPreflightLabSlotWindow(
    Date.now(),
    state === 'late' ? PREFLIGHT_LAB_LATE_START_OFFSET_MS : PREFLIGHT_LAB_SLOT_START_OFFSET_MS,
  );
  const bookingRuns = bookingsForState(state, slotWindow);

  const cohort: OralExamAggregateTimeSlotStateResponse = {
    sessionPhase: OralExamSessionPhase.pre_flight,
    capacity: PREFLIGHT_LAB_CAPACITY,
    inExamEnteredAt: null,
    admittedBookingIds: [],
    etag: 'preflight-lab-etag',
    bookingRuns,
    maxUnsupervisedSeconds: 120,
  };

  const cohortState: OralExamLabCohortState = {
    cohort,
    bookingRuns,
    capacity: PREFLIGHT_LAB_CAPACITY,
    isLoading: false,
    etag: cohort.etag ?? null,
    queryKey: ['preflight-lab-cohort', PREFLIGHT_LAB_SLOT_ID],
  };

  return {
    slotId: PREFLIGHT_LAB_SLOT_ID,
    slotStartIso: slotWindow.slotStartIso,
    slotEndIso: slotWindow.slotEndIso,
    joinSettings: {
      cameraEnabled: true,
      microphoneEnabled: false,
    },
    joinCreds: null,
    overrides: {
      cohortState,
      resolveIdPhotoUrl,
      selfViewImageUrl: PATROL_LAB_STANDINS.snape,
      examinerPresent: true,
      preflightSimulateRejectError: true,
      ...(state === 'retakeDialog'
        ? { preflightOpenRetakeBookingId: PREFLIGHT_LAB_BEN_BOOKING_ID }
        : {}),
      ...(state === 'mixed'
        ? {
          preflightColleagueStandIn: {
            identity: 'preflight-lab-colleague-mcgonagall',
            name: 'Minerva McGonagall',
            imageUrl: PATROL_LAB_STANDINS.hermione,
          },
        }
        : {}),
    },
  };
}
